import React from 'react';
import { useTenant } from '../contexts/TenantContext';
import { TrendingUp } from 'lucide-react';

/**
 * Tenant-aware Logo component
 * Shows custom logo if tenant has one, otherwise shows default Trader Titans logo
 */
const TenantLogo = ({ 
  variant = 'light', // 'light' for dark backgrounds, 'dark' for light backgrounds
  size = 'md', // 'sm', 'md', 'lg'
  showText = true,
  className = ''
}) => {
  const { branding, isWhiteLabel } = useTenant();
  
  const sizeClasses = {
    sm: { icon: 'h-6 w-6', text: 'text-lg' },
    md: { icon: 'h-8 w-8', text: 'text-xl' },
    lg: { icon: 'h-10 w-10', text: 'text-2xl' }
  };
  
  const sizes = sizeClasses[size] || sizeClasses.md;
  
  // Determine which logo URL to use
  const logoUrl = variant === 'light' 
    ? branding?.logo_light_url 
    : branding?.logo_dark_url;
  
  // If tenant has a custom logo, use it
  if (logoUrl) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <img 
          src={logoUrl} 
          alt={branding?.company_name || 'Logo'}
          className={`${sizes.icon} object-contain`}
        />
        {showText && (
          <span className={`font-bold ${sizes.text} text-white`}>
            {branding?.company_name}
          </span>
        )}
      </div>
    );
  }
  
  // Default Trader Titans logo (icon + text)
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div 
        className={`${sizes.icon} rounded-lg flex items-center justify-center`}
        style={{ backgroundColor: branding?.primary_color || '#10B981' }}
      >
        <TrendingUp className="h-5 w-5 text-white" />
      </div>
      {showText && (
        <span className={`font-bold ${sizes.text} text-white`}>
          {branding?.company_name || 'Trader Titans'}
        </span>
      )}
    </div>
  );
};

/**
 * Simple text-only logo for compact spaces
 */
export const TenantLogoText = ({ className = '' }) => {
  const { branding } = useTenant();
  
  return (
    <span className={`font-bold ${className}`}>
      {branding?.company_name || 'Trader Titans'}
    </span>
  );
};

/**
 * Icon-only logo
 */
export const TenantLogoIcon = ({ size = 'md', className = '' }) => {
  const { branding } = useTenant();
  
  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10'
  };
  
  const iconSize = sizeClasses[size] || sizeClasses.md;
  
  if (branding?.logo_light_url) {
    return (
      <img 
        src={branding.logo_light_url} 
        alt={branding?.company_name || 'Logo'}
        className={`${iconSize} object-contain ${className}`}
      />
    );
  }
  
  return (
    <div 
      className={`${iconSize} rounded-lg flex items-center justify-center ${className}`}
      style={{ backgroundColor: branding?.primary_color || '#10B981' }}
    >
      <TrendingUp className="h-5 w-5 text-white" />
    </div>
  );
};

export default TenantLogo;
